import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setCustomerAuth } from "@/lib/auth";
import { tableStore } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Coffee } from "lucide-react";
import { toast } from "sonner";

export default function CustomerLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const sessionExpired = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("session") === "expired";
  const tbl = tableStore.get();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const r = await api.post(url, payload);
      setCustomerAuth(r.data);
      toast.success(mode === "login" ? "Welcome back!" : "Account created");
      nav(-1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain grid place-items-center p-6">
      <div className="w-full max-w-sm bg-white rounded-3xl p-8 qd-shadow-soft border border-[var(--qd-border)]">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[var(--qd-primary)] grid place-items-center"><Coffee className="w-5 h-5 text-white" /></div>
          <span className="font-display font-bold text-xl">QRDine</span>
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-1">{mode === "login" ? "Welcome back" : "Create account"}</h1>
        <p className="text-sm text-[var(--qd-muted)] mb-6">Earn loyalty points on every order.</p>
        {sessionExpired && (
          <div data-testid="login-session-expired" className="mb-5 text-sm rounded-xl bg-[var(--qd-cream)] border border-[var(--qd-border)] px-4 py-3 text-[var(--qd-muted)]">
            Your session expired — please sign in again to continue.
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <>
              <Input data-testid="login-name" required placeholder="Full name" aria-label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-xl" />
              <Input data-testid="login-phone" placeholder="Phone (optional)" aria-label="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-xl" />
            </>
          )}
          <Input data-testid="login-email" required type="email" placeholder="Email" aria-label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-xl" />
          <PasswordInput data-testid="login-password" required placeholder="Password" aria-label="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-xl" />
          <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-12 rounded-xl bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] font-semibold">
            {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>
        <button data-testid="login-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 text-sm text-[var(--qd-muted)] hover:text-[var(--qd-primary)] w-full text-center">
          {mode === "login" ? "No account? Register" : "Have an account? Sign in"}
        </button>
        <button data-testid="login-cancel" onClick={() => nav(tbl ? `/m/${tbl.token}` : "/")} className="mt-2 text-xs text-[var(--qd-muted)] hover:text-[var(--qd-text)] w-full text-center">Maybe later</button>
        {mode === "login" && (
          <Link to="/forgot-password" data-testid="login-forgot" className="block mt-2 text-sm text-[var(--qd-muted)] hover:text-[var(--qd-primary)] text-center">Forgot password?</Link>
        )}
        <GoogleSignIn />
      </div>
    </div>
  );
}

// "Sign in with Google" (Google Identity Services). Renders only when
// REACT_APP_GOOGLE_CLIENT_ID is baked into the build; the backend verifies the
// ID token against the same client id and finds-or-creates the customer.
function GoogleSignIn() {
  const nav = useNavigate();
  const ref = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) return;
    const init = () => {
      if (!ref.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          try {
            const r = await api.post("/auth/google", { credential: resp.credential });
            setCustomerAuth(r.data);
            toast.success(`Welcome, ${r.data.user?.name || "friend"}!`);
            nav(-1);
          } catch (e) { toast.error(e.response?.data?.detail || "Google sign-in failed"); }
        },
      });
      window.google.accounts.id.renderButton(ref.current, { theme: "outline", size: "large", shape: "pill", width: 300 });
    };
    if (window.google?.accounts?.id) { init(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = init;
    document.head.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (!clientId) return null;
  return (
    <>
      <div className="flex items-center gap-3 my-4">
        <div className="h-px bg-[var(--qd-border)] flex-1" /><span className="text-xs text-[var(--qd-muted)]">or</span><div className="h-px bg-[var(--qd-border)] flex-1" />
      </div>
      <div ref={ref} data-testid="google-signin" className="flex justify-center" />
    </>
  );
}
